import React, { useState } from 'react';

import {
    Select as AntSelect,
    SelectProps as AntSelectProps,
    Spin as AntSpin
} from 'antd';
import cn from 'classnames';
import { useDebouncedCallback } from 'use-debounce';

import { SelectPrefix } from '@atoms/selectPrefix/SelectPrefix';
import { escapeRegExp } from '@helpers/stringHelper';
import { SvgArrow } from 'components/icons';

import styles from './SelectAutocomplete.module.less';

export interface DebounceSelectProps
    extends Omit<AntSelectProps, 'options' | 'children'> {
    fetchOptions: (search: string) => void;
    options: AntSelectProps['options'];
    debounceTimeout?: number;
    containerClassName?: string;
    isLoading: boolean;
}

export const SelectAutocomplete = ({
    fetchOptions,
    debounceTimeout = 1000,
    options,
    containerClassName,
    value,
    isLoading,
    ...props
}: DebounceSelectProps) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [showOptions, setShowOptions] = useState<boolean>(false);

    const debounceFetcher = useDebouncedCallback((value) => {
        if (value.trim().length !== 0) {
            void fetchOptions(escapeRegExp(value));
            setShowOptions(true);
        }
    }, debounceTimeout);

    const handleDropdownVisibleChange = (open: boolean) => {
        if (!open) {
            setShowOptions(false);
        }
        setIsDropdownOpen(open);
    };

    const CustomSuffixIcon = () => {
        return (
            <span
                className={`${isDropdownOpen ? styles.CustomSuffixIcon : ''}`}
            >
                <SvgArrow color="#8E94A9" />
            </span>
        );
    };

    return (
        <div
            className={cn(
                styles.Container,
                styles.Container__WithPrefix,
                containerClassName
            )}
            data-testid="container_select_prefix"
        >
            <SelectPrefix />
            <AntSelect
                labelInValue
                showSearch={true}
                filterOption={false}
                notFoundContent={
                    isLoading ? <AntSpin size="small" /> : 'No results found'
                }
                onDropdownVisibleChange={handleDropdownVisibleChange}
                {...props}
                onSearch={(value) => {
                    debounceFetcher(value);
                }}
                options={showOptions ? options : []}
                value={value}
                suffixIcon={<CustomSuffixIcon />}
                dropdownClassName={styles.SelectAutocomplete__DropDown}
                open={isDropdownOpen}
                onSelect={props.onSelect}
            />
        </div>
    );
};
